import { useMemo } from "react";
import { sumBy } from "lodash";
import { Flex } from "styles/utility-components";

import {
  isQueryableSpendFilter,
  SPEND_TIME_UNIT,
  SpendFilters,
} from "lib/hooks/api/spend/spendFilters";
import { useSpendOverTime } from "lib/hooks/api/spend/useSpendOverTime";
import { SpendExportGroupBy, SpendGroupBy } from "lib/types/graphQLEnums";
import ExportDataButton from "../ExportDataButton";
import { SpendInterval } from "../hooks/useSpendUrlState";
import { EmptyState, SpendChartError } from "../pageStates";
import { toTrendRows } from "../spendChartRows";
import { TransactionChart } from "../TransactionChart";
import { SpendSummaryTable } from "./SpendSummaryTable";

type Props = {
  filters: SpendFilters;
  interval: SpendInterval;
};

/**
 * Trends grouped by transaction date: the transactions charted over time, with the totals
 * for each period underneath. Owns the one query both read, and builds their rows once,
 * so there is a single loading, error and empty state.
 */
export function SpendOverTimeView({ filters, interval }: Props) {
  const { data, isLoading, isError, error, refetch } = useSpendOverTime(
    { filters, dateUnit: SPEND_TIME_UNIT[interval], groupBy: SpendGroupBy.None },
    { enabled: isQueryableSpendFilter(filters) },
  );

  const rows = useMemo(
    () =>
      toTrendRows(
        (data?.points ?? []).map((p) => ({
          date: p.period,
          total: p.totalValue,
          transactions: p.transactionCount,
        })),
        interval,
      ),
    [data?.points, interval],
  );
  const transactionCount = sumBy(rows, (r) => r.transactions);

  if (isError) {
    return <SpendChartError error={error} onRetry={refetch} />;
  }
  if (!isLoading && transactionCount === 0) {
    return <EmptyState filters={filters} />;
  }

  return (
    <Flex column gap={24}>
      <TransactionChart filters={filters} dateUnit={interval} rows={rows} isLoading={isLoading} />
      <SpendSummaryTable
        rows={rows}
        interval={interval}
        isLoading={isLoading}
        action={
          <ExportDataButton
            filters={filters}
            totalRows={transactionCount}
            isLoadingTotalRows={isLoading}
            groupBy={SpendExportGroupBy.None}
          />
        }
      />
    </Flex>
  );
}
